// Linter is giving false errors because of the imported shape used for proptypes
 /* eslint-disable react/default-props-match-prop-types */
 
import React from 'react';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import moment from 'moment';
import cx from 'classnames';
import { Portal } from 'react-portal';
import { addEventListener, removeEventListener } from 'consolidated-events';
import isTouchDevice from 'is-touch-device';
import { toMomentObject, toLocalizedDateString, isInclusivelyAfterDay } from 'react-dates';

import SingleDatePickerShape from './shapes/SingleDatePickerShape';

import { SingleDatePickerPhrases } from './utils/defaultPhrases';
import DateValidator from './utils/DateValidator';
import getResponsiveContainerStyles from './utils/getResponsiveContainerStyles';

import DayPickerSingleDateController from './DayPickerSingleDateController';
import OutsideClickHandler from './OutsideClickHandler';
import SingleDatePickerInput from './SingleDatePickerInput';

import { IconClose as CloseButton } from '../icons/Icons';

import { HORIZONTAL_ORIENTATION, VERTICAL_ORIENTATION, ANCHOR_LEFT, ANCHOR_RIGHT, OPEN_DOWN, OPEN_UP, DAY_SIZE, ICON_BEFORE_POSITION } from './constants';

const propTypes = {
    ...SingleDatePickerShape,
    showLabel: PropTypes.bool,
    lowerLimit: momentPropTypes.momentObj,
    upperLimit: momentPropTypes.momentObj
};

const defaultProps = {
    // required props for a functional interactive SingleDatePicker
    date: null,
    focused: false,
    showLabel: false,
    
    lowerLimit: null,
    upperLimit: null,

    // input related props
    id: 'date',
    placeholder: 'Date',
    disabled: false,
    required: false,
    readOnly: false,
    showClearDate: false,
    showDefaultInputIcon: false,
    inputIconPosition: ICON_BEFORE_POSITION,
    customInputIcon: null,
    customCloseIcon: null,

    // calendar presentation and interaction related props
    orientation: HORIZONTAL_ORIENTATION,
    anchorDirection: ANCHOR_LEFT,
    openDirection: OPEN_DOWN,
    horizontalMargin: 0,
    withPortal: false,
    withFullScreenPortal: false,
    initialVisibleMonth: null,
    firstDayOfWeek: null,
    numberOfMonths: 2,
    keepOpenOnDateSelect: false,
    reopenPickerOnClearDate: false,
    renderCalendarInfo: null,
    hideKeyboardShortcutsPanel: false,
    daySize: DAY_SIZE,
    isRTL: false,

    // navigation related props
    navPrev: null,
    navNext: null,

    onPrevMonthClick() {},
    onNextMonthClick() {},
    onClose() {},

    // month presentation and interaction related props
    renderMonth: null,

    // day presentation and interaction related props
    renderDay: null,
    enableOutsideDays: false,
    isDayBlocked: () => false,
    isOutsideRange: (day) => !isInclusivelyAfterDay(day, moment()),
    isDayHighlighted: () => {},

    // internationalization props
    displayFormat: () => moment.localeData().longDateFormat('L'),
    monthFormat: 'MMMM YYYY',
    weekDayFormat: 'dd',
    phrases: SingleDatePickerPhrases
};

export default class SingleDatePicker extends React.Component {
    constructor(props) {
        super(props);

        this.isTouchDevice = false;

        this.state = {
            dayPickerContainerStyles: {},
            isDayPickerFocused: false,
            isInputFocused: !!props.focused,
            errorMessage: '',
            displayErrorDate: ''
        };

        this.onDayPickerFocus = this.onDayPickerFocus.bind(this);
        this.onDayPickerBlur = this.onDayPickerBlur.bind(this);

        this.onChange = this.onChange.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onClearFocus = this.onClearFocus.bind(this);
        this.clearDate = this.clearDate.bind(this);

        this.responsivizePickerPosition = this.responsivizePickerPosition.bind(this);

        this.setDayPickerContainerRef = this.setDayPickerContainerRef.bind(this);
    }

    /* istanbul ignore next */
    componentDidMount() {
        this.resizeHandle = addEventListener(window, 'resize', this.responsivizePickerPosition, { passive: true });
        this.responsivizePickerPosition();
        this.isTouchDevice = isTouchDevice();
    }
    
    componentWillReceiveProps = (nextProps) => {
        if (nextProps.date && nextProps.date !== this.props.date) {
            this.setState({
                errorMessage: '',
                displayErrorDate: ''
            })
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.focused && this.props.focused) {
            this.responsivizePickerPosition();
        }
    }

    /* istanbul ignore next */
    componentWillUnmount() {
        removeEventListener(this.resizeHandle);
    }

    onChange(dateString) {
        const { isOutsideRange, keepOpenOnDateSelect, onDateChange, onFocusChange, onClose } = this.props;
        const newDate = toMomentObject(dateString, this.getDisplayFormat());
        
        const isValid = newDate && !isOutsideRange(newDate);
        if (isValid) {
            onDateChange(newDate);
            this.setState({
                errorMessage: ''
            });
            if (!keepOpenOnDateSelect) {
                onFocusChange({ focused: false });
                onClose({ date: newDate });
            }
        }
        else {
            onDateChange(null);
        }
    }

    onBlur = (value) => {
        const { onDateChange, isOutsideRange, isDayBlocked, lowerLimit, upperLimit, date } = this.props;
        if (!value && !date && !this.state.errorMessage) {
            // Don't fire onChange if there's no change
            return
        }
        
        // If empty, reset validation and clear date
        if (typeof value !== 'string' || value === '') {
            this.setState({
                errorMessage: ''
            });
            onDateChange(null);
            return;
        }
        
        const parsed = DateValidator({
            input: value,
            lowerLimit: lowerLimit || moment(),
            upperLimit: upperLimit || moment().add(10, 'years')
        });
        
        if (parsed.isValid && moment(date).isSame(parsed.moment, 'day')) {
            // Is same day so don't update ... likely it's the user clicking the
            // calendar picker
            return;
        }

        if (!parsed.isValid) {
            this.setState({
                errorMessage: parsed.errorMessage
            });
            onDateChange(null);
            
            if (parsed.isValidMoment) {
                this.setState({
                    displayErrorDate: this.getDateString(parsed.moment)
                });
            }
        }
        else if (isOutsideRange(parsed.moment) || isDayBlocked(parsed.moment)) {
            this.setState({
                errorMessage: 'is not available.'
            });
            onDateChange(null);
            
            if (parsed.isValidMoment) {
                this.setState({
                    displayErrorDate: this.getDateString(parsed.moment)
                });
            }
        }
        else {
            onDateChange(parsed.moment.startOf('day'));
            this.setState({
                errorMessage: ''
            });
        }
    };

    onFocus() {
        const { disabled, onFocusChange, withPortal, withFullScreenPortal } = this.props;

        const moveFocusToDayPicker = withPortal || withFullScreenPortal || this.isTouchDevice;
        if (moveFocusToDayPicker) {
            this.onDayPickerFocus();
        }
        else {
            this.onDayPickerBlur();
        }

        if (!disabled) {
            onFocusChange({ focused: true });
        }
    }

    onClearFocus(value) {
        this.onBlur(value);
        
        const { startDate, endDate, focused, onFocusChange, onClose } = this.props;
        if (!focused) return;

        this.setState({
            isInputFocused: false,
            isDayPickerFocused: false
        });

        onFocusChange({ focused: false });
        onClose({ startDate, endDate });
    }
    
    onOutsideClick = () => {
        const { startDate, endDate, focused, onFocusChange, onClose } = this.props;
        if (!focused) return;

        this.setState({
            isInputFocused: false,
            isDayPickerFocused: false
        });

        onFocusChange({ focused: false });
        onClose({ startDate, endDate });
    }

    onDayPickerFocus() {
        this.setState({
            isInputFocused: false,
            isDayPickerFocused: true
        });
    }

    onDayPickerBlur() {
        this.setState({
            isInputFocused: true,
            isDayPickerFocused: false
        });
    }

    getDateString(date) {
        const displayFormat = this.getDisplayFormat();
        if (date && displayFormat) {
            return date && date.format(displayFormat);
        }
        return toLocalizedDateString(date);
    }

    getDayPickerContainerClasses() {
        const { orientation, withPortal, withFullScreenPortal, anchorDirection, openDirection, isRTL } = this.props;

        const dayPickerClassName = cx('SingleDatePicker__picker', {
            'SingleDatePicker__picker--direction-left': anchorDirection === ANCHOR_LEFT,
            'SingleDatePicker__picker--direction-right': anchorDirection === ANCHOR_RIGHT,
            'SingleDatePicker__picker--open-down': openDirection === OPEN_DOWN,
            'SingleDatePicker__picker--open-up': openDirection === OPEN_UP,
            'SingleDatePicker__picker--horizontal': orientation === HORIZONTAL_ORIENTATION,
            'SingleDatePicker__picker--vertical': orientation === VERTICAL_ORIENTATION,
            'SingleDatePicker__picker--portal': withPortal || withFullScreenPortal,
            'SingleDatePicker__picker--full-screen-portal': withFullScreenPortal,
            'SingleDatePicker__picker--rtl': isRTL
        });

        return dayPickerClassName;
    }

    getDisplayFormat() {
        const { displayFormat } = this.props;
        return typeof displayFormat === 'string' ? displayFormat : displayFormat();
    }

    setDayPickerContainerRef(ref) {
        this.dayPickerContainer = ref;
    }

    clearDate() {
        const { onDateChange, reopenPickerOnClearDate, onFocusChange } = this.props;
        onDateChange(null);
        
        this.setState({
            errorMessage: '',
            displayErrorDate: ''
        });
        
        if (reopenPickerOnClearDate) {
            onFocusChange({ focused: true });
        }
    }

    /* istanbul ignore next */
    responsivizePickerPosition() {
        // It's possible the portal props have been changed in response to window resizes
        // So let's ensure we reset this back to the base state each time
        this.setState({ dayPickerContainerStyles: {} });

        const { anchorDirection, horizontalMargin, withPortal, withFullScreenPortal, focused } = this.props;
        const { dayPickerContainerStyles } = this.state;

        if (!focused) {
            return;
        }

        const isAnchoredLeft = anchorDirection === ANCHOR_LEFT;

        if (!withPortal && !withFullScreenPortal) {
            const containerRect = this.dayPickerContainer.getBoundingClientRect();
            const currentOffset = dayPickerContainerStyles[anchorDirection] || 0;
            const containerEdge = isAnchoredLeft ? containerRect[ANCHOR_RIGHT] : containerRect[ANCHOR_LEFT];

            this.setState({
                dayPickerContainerStyles: getResponsiveContainerStyles(anchorDirection, currentOffset, containerEdge, horizontalMargin)
            });
        }
    }

    maybeRenderDayPickerWithPortal() {
        const { focused, withPortal, withFullScreenPortal } = this.props;

        if (!focused) {
            return null;
        }

        if (withPortal || withFullScreenPortal) {
            return <Portal isOpened>{this.renderDayPicker()}</Portal>;
        }

        return this.renderDayPicker();
    }

    renderDayPicker() {
        const {
            onDateChange,
            date,
            onFocusChange,
            focused,
            enableOutsideDays,
            numberOfMonths,
            orientation,
            monthFormat,
            navPrev,
            navNext,
            onPrevMonthClick,
            onNextMonthClick,
            withPortal,
            withFullScreenPortal,
            keepOpenOnDateSelect,
            initialVisibleMonth,
            renderMonth,
            renderDay,
            renderCalendarInfo,
            hideKeyboardShortcutsPanel,
            firstDayOfWeek,
            customCloseIcon,
            phrases,
            daySize,
            isRTL,
            isOutsideRange,
            isDayBlocked,
            isDayHighlighted,
            weekDayFormat
        } = this.props;
        const { dayPickerContainerStyles, isDayPickerFocused } = this.state;

        const onOutsideClick = !withFullScreenPortal && withPortal ? this.onOutsideClick : undefined;
        const closeIcon = customCloseIcon || <CloseButton />;

        return (
            // eslint-disable jsx-a11y/no-static-element-interactions jsx-a11y/click-events-have-key-events
            <div
                ref={this.setDayPickerContainerRef}
                className={this.getDayPickerContainerClasses()}
                style={dayPickerContainerStyles}
                onClick={onOutsideClick}
                role="presentation">
                <DayPickerSingleDateController
                    date={date}
                    onDateChange={onDateChange}
                    onFocusChange={onFocusChange}
                    orientation={orientation}
                    enableOutsideDays={enableOutsideDays}
                    numberOfMonths={numberOfMonths}
                    monthFormat={monthFormat}
                    withPortal={withPortal || withFullScreenPortal}
                    focused={focused}
                    keepOpenOnDateSelect={keepOpenOnDateSelect}
                    hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                    initialVisibleMonth={initialVisibleMonth}
                    navPrev={navPrev}
                    navNext={navNext}
                    onPrevMonthClick={onPrevMonthClick}
                    onNextMonthClick={onNextMonthClick}
                    renderMonth={renderMonth}
                    renderDay={renderDay}
                    renderCalendarInfo={renderCalendarInfo}
                    isFocused={isDayPickerFocused}
                    phrases={phrases}
                    daySize={daySize}
                    isRTL={isRTL}
                    isOutsideRange={isOutsideRange}
                    isDayBlocked={isDayBlocked}
                    isDayHighlighted={isDayHighlighted}
                    firstDayOfWeek={firstDayOfWeek}
                    weekDayFormat={weekDayFormat} />

                {withFullScreenPortal && (
                    <button aria-label={phrases.closeDatePicker} className="SingleDatePicker__close" type="button" onClick={this.onClearFocus}>
                        <div className="SingleDatePicker__close-icon">{closeIcon}</div>
                    </button>
                )}
            </div>
        );
    }

    render() {
        const {
            id,
            placeholder,
            disabled,
            focused,
            required,
            readOnly,
            openDirection,
            showClearDate,
            showDefaultInputIcon,
            inputIconPosition,
            customInputIcon,
            date,
            phrases,
            withPortal,
            withFullScreenPortal,
            showLabel,
            isRTL
        } = this.props;

        const { errorMessage } = this.state;

        const { isInputFocused } = this.state;

        const dateString = this.state.displayErrorDate || this.getDateString(date);

        const onOutsideClick = !withPortal && !withFullScreenPortal ? this.onOutsideClick : undefined;

        return (
            <div className="SingleDatePicker">
                <OutsideClickHandler onOutsideClick={onOutsideClick}>
                    <SingleDatePickerInput
                        id={id}
                        showLabel={showLabel}
                        placeholder={placeholder}
                        focused={focused}
                        isFocused={isInputFocused}
                        disabled={disabled}
                        required={required}
                        readOnly={readOnly}
                        openDirection={openDirection}
                        showCaret={!withPortal && !withFullScreenPortal}
                        onClearDate={this.clearDate}
                        showClearDate={showClearDate}
                        showDefaultInputIcon={showDefaultInputIcon}
                        inputIconPosition={inputIconPosition}
                        customInputIcon={customInputIcon}
                        inputValue={dateString}
                        errorMessage={errorMessage}
                        onBlur={this.onBlur}
                        onFocus={this.onFocus}
                        onKeyDownShiftTab={this.onClearFocus}
                        onKeyDownTab={this.onClearFocus}
                        onKeyDownArrowDown={this.onDayPickerFocus}
                        phrases={phrases}
                        isRTL={isRTL} />

                    {this.maybeRenderDayPickerWithPortal()}
                </OutsideClickHandler>
            </div>
        );
    }
}

SingleDatePicker.propTypes = propTypes;
SingleDatePicker.defaultProps = defaultProps;
